import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import useSeo from "../hooks/useSeo";
import { PublicHeader } from "../components/PublicHeader";
import { SiteFooter } from "../components/SiteFooter";

// Plain-English privacy policy. Linked from /sign-in (under disclaimer link)
// and from Settings. Written to be HONEST about everything we collect — see
// /app/frontend/ios/APP_STORE_PRIVACY.md for the matching App Store
// nutrition-label answers.

const Section = ({ title, children }) => (
  <section className="mb-9">
    <h2 className="font-outfit font-semibold text-xl text-[#1B2349] mb-3">
      {title}
    </h2>
    <div className="text-[#4A5568] leading-relaxed space-y-3">
      {children}
    </div>
  </section>
);

const PrivacyPolicyPage = () => {
  const navigate = useNavigate();
  useSeo({
    title: "Privacy Policy — Bilateral Calm",
    description:
      "Bilateral Calm's privacy policy: what we collect, how we use it, and your data-export and account-deletion rights.",
    path: "/privacy",
  });
  return (
    <div className="min-h-screen bg-[#FBF8F1]" data-testid="privacy-policy-page">
      <PublicHeader />
      <div className="bg-white/40 border-y border-slate-100/60">
        <div className="max-w-3xl mx-auto px-6 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-full hover:bg-white/60"
            aria-label="Back"
            data-testid="privacy-back-btn"
          >
            <ArrowLeft className="w-5 h-5 text-[#1B2349]" />
          </button>
          <h1 className="font-outfit font-semibold text-lg text-[#1B2349]">
            Privacy Policy
          </h1>
        </div>
      </div>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <p className="text-sm text-[#718096] mb-3">Effective: February 2026</p>
        <p className="text-[#4A5568] leading-relaxed mb-4" data-testid="privacy-controller-statement">
          <strong>Bilateral Calm</strong> is operated by{" "}
          <strong>Mood and Sleep Clinic LLC</strong>, which acts as the data controller for the
          information described below. Questions or requests: email{" "}
          <a
            href="mailto:mscwellness@protonmail.com"
            className="text-[#3090A8] underline underline-offset-2"
            data-testid="privacy-controller-email"
          >
            mscwellness@protonmail.com
          </a>
          .
        </p>
        <p className="text-[#4A5568] leading-relaxed mb-10">
          Bilateral Calm is a self-help app. We try to collect as little as
          possible, only what's needed to run your account and your subscription.
          This page lists every piece of data we hold, why we hold it, and
          how to get rid of it.
        </p>

        <Section title="What we collect">
          <p>From your Google sign-in: your <strong>email address, name, and profile picture</strong>.</p>
          <p>Created automatically as you use the app:</p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>Your subscription status, plan, and renewal dates.</li>
            <li>Which practices you start, pause, and finish.</li>
            <li>Your distress slider check-ins (e.g. "tension at 4/10") so the practice can adapt and so you can see your progress over time.</li>
            <li>Reminders and favorites you set yourself.</li>
            <li>If you opt into Face ID / passkey login, an opaque credential id (not your face data — that stays on your device).</li>
            <li>If you opt into push notifications on iOS, an Apple-issued device token.</li>
          </ul>
          <p>We do <strong>not</strong> collect: your contacts, your location, your microphone, your camera, your health data, or anything else outside the bullets above.</p>
        </Section>

        <Section title="Who we share it with">
          <p>
            We share data with these third parties only when needed to make
            the app work:
          </p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li><strong>Google</strong> (sign-in) — verifies your identity. Their privacy policy applies to that step.</li>
            <li><strong>Stripe</strong> (web subscriptions) — handles payment. We pass your email and a customer id; Stripe holds the card details, not us.</li>
            <li><strong>Apple / RevenueCat</strong> (iOS subscriptions) — handles in-app purchases on iPhone. RevenueCat receives a per-app user id and the transaction event.</li>
          </ul>
          <p>We do <strong>not</strong> sell, rent, or trade any of your data. We do not run advertising.</p>
        </Section>

        <Section title="How long we keep it">
          <p>
            Account, subscription, and practice data live for as long as your
            account exists. If you delete your account (see below), we wipe
            everything immediately. Payment records are retained in an
            anonymised form (no name or email — just amount + timestamp) for
            7 years to satisfy tax and accounting laws.
          </p>
        </Section>

        <Section title="Your rights — exercise them in Settings">
          <p>
            From <a href="/settings" className="text-[#3090A8] underline underline-offset-2">Settings → Privacy</a> you can:
          </p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li><strong>Download my data</strong> — gives you a JSON file with everything we hold on you.</li>
            <li><strong>Delete my account</strong> — wipes your data immediately. This is permanent.</li>
          </ul>
          <p>
            These cover GDPR Articles 15 and 17 (EU/UK) and CCPA §§ 1798.100
            and 1798.105 (California).
          </p>
        </Section>

        <Section title="Children">
          <p>
            Bilateral Calm is intended for users 18 and older. We don't
            knowingly collect data from anyone younger. If you believe a
            child has registered, email the address above and we'll remove
            the account.
          </p>
        </Section>

        <Section title="Changes">
          <p>
            If we ever change this policy, we'll show a notice on the
            dashboard so you have a chance to review before continuing.
          </p>
        </Section>
      </main>
      <SiteFooter />
    </div>
  );
};

export default PrivacyPolicyPage;
